import React, { useState, useEffect } from 'react';
import { BarChart3, Download, Clock, MapPin, FileSpreadsheet, FileText } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import { clientAPI } from '../services/api';
import { getAppointments } from '../services/appointmentApi';
import { getPropertiesByClient } from '../services/propertyApi';
import { getAllZones } from '../services/zoneApi';
import type { Zone } from '../services/zoneApi';
import PhoneLink from '../components/PhoneLink';
import TimeDiscrepanciesReport from './TimeDiscrepanciesReport';
import { exportToCSV, exportToExcel, exportToJSON, exportToPDF } from '../utils/exportUtils';
import ServiceSuggestionsPanel from '../components/ServiceSuggestionsPanel';
import { buildServiceSuggestions, type ServiceSuggestion } from '../utils/serviceSuggestions';

interface TimeDiscrepancy {
  appointment_id: string;
  service_type: string;
  client_name: string;
  scheduled_date: string;
  estimated_duration: number;
  actual_duration: number;
  difference: number;
  status: 'over' | 'under' | 'exact';
}

interface ReportData {
  monthlyRevenue: { month: string; revenue: number }[];
  serviceTypes: { type: string; count: number; revenue: number }[];
  topClients: { id: string; name: string; email?: string; phone: string; services: number; revenue: number }[];
  timeDiscrepancies: TimeDiscrepancy[];
}

const Relatorios: React.FC = () => {
  const { t } = useI18n();
  const [reportData, setReportData] = useState<ReportData>({
    monthlyRevenue: [],
    serviceTypes: [],
    topClients: [],
    timeDiscrepancies: []
  });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('6months');
  const [clientSortBy, setClientSortBy] = useState<'name' | 'revenue'>('revenue');
  const [activeReport, setActiveReport] = useState<'main' | 'discrepancies'>('main');
  const [zones, setZones] = useState<Zone[]>([]);
  const [selectedZone, setSelectedZone] = useState<string>('');
  const [serviceSuggestions, setServiceSuggestions] = useState<ServiceSuggestion[]>([]);

  useEffect(() => {
    // Carregar zonas ao montar componente
    const loadZones = async () => {
      try {
        const zonesData = await getAllZones(true); // Apenas zonas ativas
        setZones(zonesData);
      } catch (error) {
        console.error('Erro ao carregar zonas:', error);
      }
    };
    loadZones();
  }, []);

  const loadReportData = async () => {
    setIsLoading(true);
    try {
      // Buscar appointments completados
      let completedAppointments = await getAppointments({ status: 'completed' });
      let appointmentsArray = Array.isArray(completedAppointments)
        ? completedAppointments
        : (completedAppointments?.appointments || completedAppointments?.data || []);

      // Filtrar por zona se selecionada
      if (selectedZone) {
        // Buscar funcionários da zona selecionada
        try {
          const { listActiveEmployees } = await import('../services/employeeApi');
          const selectedZoneObj = zones.find(z => z.id === selectedZone || z.name === selectedZone);
          if (selectedZoneObj) {
            const employeesInZone = await listActiveEmployees(selectedZoneObj.name);
            const employeeIds = new Set(employeesInZone.map(emp => emp.id));
            
            // Filtrar appointments por funcionários da zona
            appointmentsArray = appointmentsArray.filter((apt: any) => 
              apt.employee_id && employeeIds.has(apt.employee_id)
            );
          }
        } catch (error) {
          console.warn('Erro ao filtrar por zona:', error);
        }
      }

      // Buscar todos os clientes
      const clientsResponse = await clientAPI.get('/clients');
      const allClients = clientsResponse.data.clients || [];

      // Calcular receita mensal (últimos 6 meses)
      const monthlyRevenueMap = new Map<string, number>();
      const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
      
      appointmentsArray.forEach((apt: any) => {
        if (apt.price && (apt.scheduled_date || apt.end_time)) {
          // Usar end_time quando disponível (completado), senão scheduled_date
          const date = apt.end_time ? new Date(apt.end_time) : new Date(apt.scheduled_date);
          const monthKey = `${monthNames[date.getMonth()]}`;
          monthlyRevenueMap.set(monthKey, (monthlyRevenueMap.get(monthKey) || 0) + (apt.price || 0));
        }
      });

      // Criar array de receita mensal (últimos 6 meses)
      const now = new Date();
      const monthlyRevenue: { month: string; revenue: number }[] = [];
      for (let i = 5; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthName = monthNames[date.getMonth()];
        monthlyRevenue.push({
          month: monthName,
          revenue: monthlyRevenueMap.get(monthName) || 0
        });
      }

      // Calcular por tipo de serviço
      const serviceTypeMap = new Map<string, { count: number; revenue: number }>();
      appointmentsArray.forEach((apt: any) => {
        const serviceType = apt.service_type || 'limpeza';
        const current = serviceTypeMap.get(serviceType) || { count: 0, revenue: 0 };
        serviceTypeMap.set(serviceType, {
          count: current.count + 1,
          revenue: current.revenue + (apt.price || 0)
        });
      });

      const serviceTypes = Array.from(serviceTypeMap.entries()).map(([type, data]) => ({
        type: type.charAt(0).toUpperCase() + type.slice(1),
        count: data.count,
        revenue: data.revenue
      }));

      // Buscar todas as properties para mapear property_id -> client_id
      const propertyToClientMap = new Map<string, string>();
      try {
        // Buscar properties de todos os clientes
        for (const client of allClients) {
          const properties = await getPropertiesByClient(client.id);
          properties.forEach((prop: any) => {
            propertyToClientMap.set(prop.id, client.id);
          });
        }
      } catch (error) {
        console.warn('Erro ao buscar properties para mapear clientes:', error);
      }

      // Calcular receita por cliente usando property_id -> client_id
      const clientRevenueMap = new Map<string, { services: number; revenue: number }>();
      appointmentsArray.forEach((apt: any) => {
        if (apt.property_id) {
          const clientId = propertyToClientMap.get(apt.property_id);
          if (clientId) {
            const current = clientRevenueMap.get(clientId) || { services: 0, revenue: 0 };
            clientRevenueMap.set(clientId, {
              services: current.services + 1,
              revenue: current.revenue + (apt.price || 0)
            });
          }
        }
      });

      // Mapear clientes com receita
      const clientsWithRevenue = allClients.map((client: any) => {
        const clientData = clientRevenueMap.get(client.id) || { services: 0, revenue: 0 };
        return {
          id: client.id,
          name: client.name,
          email: client.email,
          phone: client.phone,
          services: clientData.services,
          revenue: clientData.revenue
        };
      }).filter((client: any) => client.services > 0); // Apenas clientes com serviços

      // Garantir que actual_duration e end_time sejam mapeados corretamente
      const normalizedAppointments = appointmentsArray.map((apt: any) => ({
        ...apt,
        actual_duration: apt.actual_duration || apt.ActualDuration || apt.actualDuration,
        end_time: apt.end_time || apt.EndTime || apt.endTime,
        duration: apt.duration || apt.Duration,
        status: apt.status || apt.Status
      }));
      
      // Calcular discrepâncias de tempo
      const timeDiscrepancies: TimeDiscrepancy[] = [];
      normalizedAppointments.forEach((apt: any) => {
        if (apt.actual_duration && apt.duration && apt.status === 'completed') {
          const difference = apt.actual_duration - apt.duration;
          timeDiscrepancies.push({
            appointment_id: apt.id,
            service_type: apt.service_type || 'limpeza',
            client_name: apt.client_name || 'Cliente não informado',
            scheduled_date: apt.scheduled_date || apt.end_time,
            estimated_duration: apt.duration,
            actual_duration: apt.actual_duration,
            difference,
            status: difference > 0 ? 'over' : difference < 0 ? 'under' : 'exact'
          });
        }
      });

      // Ordenar por maior discrepância (positiva primeiro)
      timeDiscrepancies.sort((a, b) => {
        if (a.status === 'over' && b.status !== 'over') return -1;
        if (a.status !== 'over' && b.status === 'over') return 1;
        return Math.abs(b.difference) - Math.abs(a.difference);
      });

      const suggestions = buildServiceSuggestions(normalizedAppointments).slice(0, 6);

      setReportData({
        monthlyRevenue,
        serviceTypes,
        topClients: clientsWithRevenue.sort((a: any, b: any) => b.revenue - a.revenue),
        timeDiscrepancies: timeDiscrepancies.slice(0, 20) // Top 20 discrepâncias
      });
      setServiceSuggestions(suggestions);
      setIsLoading(false);
    } catch (error) {
      console.error('Erro ao carregar relatórios:', error);
      setIsLoading(false);
    }
  };

  const generateReport = (type: string, format: 'json' | 'csv' | 'excel' | 'pdf' = 'json') => {
    try {
      let exportData: any = {};
      let filename = '';
      let headers: string[] = [];
      let dataArray: any[] = [];
      
      switch (type) {
        case 'receita-mensal':
          headers = ['Mês', 'Receita (R$)'];
          dataArray = reportData.monthlyRevenue.map(item => ({
            'Mês': item.month,
            'Receita (R$)': item.revenue
          }));
          exportData = {
            title: t('reportMonthlyRevenue'),
            period: selectedPeriod,
            data: reportData.monthlyRevenue,
            generatedAt: new Date().toISOString(),
            total: reportData.monthlyRevenue.reduce((sum: number, item: any) => sum + item.revenue, 0)
          };
          filename = `receita-mensal-${new Date().toISOString().split('T')[0]}`;
          break;
          
        case 'tipos-servico':
          headers = ['Tipo de Serviço', 'Quantidade', 'Receita (R$)'];
          dataArray = reportData.serviceTypes.map(item => ({
            'Tipo de Serviço': item.type,
            'Quantidade': item.count,
            'Receita (R$)': item.revenue
          }));
          exportData = {
            title: t('reportServiceTypes'),
            period: selectedPeriod,
            data: reportData.serviceTypes,
            generatedAt: new Date().toISOString(),
            totalServices: reportData.serviceTypes.reduce((sum: number, item: any) => sum + item.count, 0),
            totalRevenue: reportData.serviceTypes.reduce((sum: number, item: any) => sum + item.revenue, 0)
          };
          filename = `tipos-servico-${new Date().toISOString().split('T')[0]}`;
          break;
          
        case 'top-clientes':
          headers = ['Cliente', 'Serviços', 'Receita (R$)'];
          dataArray = reportData.topClients.map(item => ({
            'Cliente': item.name,
            'Serviços': item.services,
            'Receita (R$)': item.revenue
          }));
          exportData = {
            title: t('reportTopClients'),
            period: selectedPeriod,
            data: reportData.topClients,
            generatedAt: new Date().toISOString(),
            totalClients: reportData.topClients.length,
            totalRevenue: reportData.topClients.reduce((sum: number, item: any) => sum + item.revenue, 0)
          };
          filename = `top-clientes-${new Date().toISOString().split('T')[0]}`;
          break;
          
        default:
          throw new Error(t('reportTypeNotRecognized'));
      }
      
      // Exportar no formato solicitado
      switch (format) {
        case 'json':
          exportToJSON(exportData, filename);
          break;
        case 'csv':
          exportToCSV(dataArray, filename, headers);
          break;
        case 'excel':
          exportToExcel(dataArray, filename, headers, type);
          break;
        case 'pdf':
          const htmlTable = `
            <table>
              <thead>
                <tr>
                  ${headers.map(h => `<th>${h}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${dataArray.map(row => `
                  <tr>
                    ${headers.map(h => `<td>${row[h] || ''}</td>`).join('')}
                  </tr>
                `).join('')}
              </tbody>
            </table>
          `;
          exportToPDF(exportData.title || type, htmlTable, filename);
          break;
      }
      
      alert(t('reportGeneratedSuccess')?.replace('{type}', type) || 'Relatório gerado com sucesso!');
      
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      alert(t('reportGenerationError'));
    }
  };

  const generatePDFReport = (type: string) => {
    try {
      // Criar conteúdo HTML para o relatório
      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Relatório ${type}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 10px; }
            .section { margin-bottom: 20px; }
            .section h3 { color: #333; border-bottom: 1px solid #ccc; padding-bottom: 5px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Relatório de ${type.replace('-', ' ').toUpperCase()}</h1>
            <p>Gerado em: ${new Date().toLocaleString('pt-BR')}</p>
            <p>Período: ${selectedPeriod}</p>
          </div>
      `;

      switch (type) {
        case 'receita-mensal':
          htmlContent += `
            <div class="section">
              <h3>{t('reports.monthlyRevenue')}</h3>
              <table>
                <thead>
                  <tr><th>{t('common.month')}</th><th>{t('reports.monthlyRevenueCol')}</th></tr>
                </thead>
                <tbody>
                  ${reportData.monthlyRevenue.map((item: any) => 
                    `<tr><td>${item.month}</td><td>R$ ${item.revenue.toLocaleString('pt-BR')}</td></tr>`
                  ).join('')}
                </tbody>
              </table>
              <p><strong>Total: R$ ${reportData.monthlyRevenue.reduce((sum: number, item: any) => sum + item.revenue, 0).toLocaleString('pt-BR')}</strong></p>
            </div>
          `;
          break;
          
        case 'tipos-servico':
          htmlContent += `
            <div class="section">
              <h3>{t('reports.serviceTypes')}</h3>
              <table>
                <thead>
                  <tr><th>{t('reports.type')}</th><th>{t('common.quantity')}</th><th>{t('reports.monthlyRevenueCol')}</th></tr>
                </thead>
                <tbody>
                  ${reportData.serviceTypes.map((item: any) => 
                    `<tr><td>${item.type}</td><td>${item.count}</td><td>R$ ${item.revenue.toLocaleString('pt-BR')}</td></tr>`
                  ).join('')}
                </tbody>
              </table>
              <p><strong>Total de Serviços: ${reportData.serviceTypes.reduce((sum: number, item: any) => sum + item.count, 0)}</strong></p>
              <p><strong>Receita Total: R$ ${reportData.serviceTypes.reduce((sum: number, item: any) => sum + item.revenue, 0).toLocaleString('pt-BR')}</strong></p>
            </div>
          `;
          break;
          
        case 'top-clientes':
          htmlContent += `
            <div class="section">
              <h3>{t('reports.topClients')}</h3>
              <table>
                <thead>
                  <tr><th>{t('common.position')}</th><th>{t('reports.client')}</th><th>{t('reports.services')}</th><th>{t('reports.monthlyRevenueCol')}</th></tr>
                </thead>
                <tbody>
                  ${reportData.topClients.map((item: any, index: number) => 
                    `<tr><td>${index + 1}</td><td>${item.name}</td><td>${item.services}</td><td>R$ ${item.revenue.toLocaleString('pt-BR')}</td></tr>`
                  ).join('')}
                </tbody>
              </table>
              <p><strong>Total de Clientes: ${reportData.topClients.length}</strong></p>
              <p><strong>Receita Total: R$ ${reportData.topClients.reduce((sum: number, item: any) => sum + item.revenue, 0).toLocaleString('pt-BR')}</strong></p>
            </div>
          `;
          break;
      }

      htmlContent += `
          <div class="footer">
            <p>{t('reports.generatedBy')}</p>
            <p>Para mais informações, acesse o sistema em http://localhost:5173</p>
          </div>
        </body>
        </html>
      `;

      // Criar e baixar o arquivo HTML
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio-${type}-${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      alert(`Relatório ${type} em HTML gerado e baixado com sucesso!`);
      
    } catch (error) {
      console.error('Erro ao gerar relatório PDF:', error);
      alert(t('reports.errorGenerate'));
    }
  };

  useEffect(() => {
    loadReportData();
  }, [selectedPeriod, selectedZone]);

  // Ordenar clientes baseado na seleção
  const sortedClients = [...reportData.topClients].sort((a, b) => {
    if (clientSortBy === 'name') {
      return a.name.localeCompare(b.name);
    } else {
      return b.revenue - a.revenue; // Ordem decrescente por valor
    }
  });

  const maxRevenue = Math.max(...reportData.monthlyRevenue.map(r => r.revenue));

  // Se o relatório de discrepâncias estiver ativo, renderizar ele
  if (activeReport === 'discrepancies') {
    return (
      <div>
        <div style={{ padding: '20px 30px', borderBottom: '1px solid #e5e7eb', backgroundColor: 'white' }}>
          <button
            onClick={() => setActiveReport('main')}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f3f4f6',
              border: '1px solid #ddd',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
            }}
          >{t('timeDiscrepancies.backToReports')}</button>
        </div>
        <TimeDiscrepanciesReport />
      </div>
    );
  }

  return (
    <div style={{ padding: '30px' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ margin: '0 0 10px 0', color: '#333' }}>{t('reportsAndAnalytics')}</h1>
        <p style={{ color: '#666', margin: 0 }}>{t('reportsSubtitle')}</p>
      </div>

      {/* Links para relatórios específicos */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginBottom: '30px',
        }}
      >
        <button
          onClick={() => setActiveReport('discrepancies')}
          style={{
            backgroundColor: 'white',
            border: '2px solid #3b82f6',
            borderRadius: '8px',
            padding: '20px',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#eff6ff';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <Clock size={32} style={{ color: '#3b82f6' }} />
          <h3 style={{ margin: 0, color: '#333' }}>{t('reports.timeDiscrepancies')}</h3>
          <p style={{ margin: 0, color: '#666', fontSize: '14px', textAlign: 'center' }}>{t('timeDiscrepancies.subtitle')}</p>
        </button>
      </div>

      {/* Filtros */}
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          padding: '20px',
          marginBottom: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontWeight: 'bold' }}>{t('period')}:</label>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            style={{
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              outline: 'none',
            }}
          >
            <option value="3months">{t('last3Months')}</option>
            <option value="6months">{t('last6Months')}</option>
            <option value="1year">{t('lastYear')}</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <label
            style={{
              fontWeight: 'bold',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <MapPin size={16} />
            {t('reports.filterByZone') || 'Zona'}:
          </label>
          <select
            value={selectedZone}
            onChange={(e) => setSelectedZone(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              outline: 'none',
              fontSize: '14px',
              minWidth: '200px',
            }}
          >
            <option value="">{t('reports.allZones') || 'Todas as zonas'}</option>
            {zones.map((zone) => (
              <option key={zone.id} value={zone.id}>
                {zone.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Receita Mensal */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, color: '#333' }}>{t('monthlyRevenue')}</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => generateReport('receita-mensal')}
              style={{
                backgroundColor: '#9333ea',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Download style={{ fontSize: '16px' }} />
              JSON
            </button>
            <button
              onClick={() => generatePDFReport('receita-mensal')}
              style={{
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Download style={{ fontSize: '16px' }} />
              HTML
            </button>
          </div>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div>{t('common.loadingChart')}</div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'end', gap: '20px', height: '200px', padding: '20px 0' }}>
            {reportData.monthlyRevenue.map((item, index) => (
              <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div
                  style={{
                    width: '100%',
                    height: `${(item.revenue / maxRevenue) * 150}px`,
                    backgroundColor: '#9333ea',
                    borderRadius: '4px 4px 0 0',
                    marginBottom: '10px',
                    minHeight: '20px'
                  }}
                />
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                  R$ {item.revenue.toLocaleString('pt-BR')}
                </div>
                <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                  {item.month}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tipos de Serviço */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, color: '#333' }}>{t('servicesByType')}</h3>
          <button
            onClick={() => generateReport('tipos-servico')}
            style={{
              backgroundColor: '#9333ea',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Download style={{ fontSize: '16px' }} />
            {t('download')}
          </button>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div>{t('common.loadingData')}</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
            {reportData.serviceTypes.map((service, index) => (
              <div
                key={index}
                style={{
                  border: '1px solid #eee',
                  borderRadius: '8px',
                  padding: '15px',
                  backgroundColor: '#f9f9f9'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h4 style={{ margin: 0, color: '#333' }}>{service.type}</h4>
                  <BarChart3 style={{ color: '#9333ea' }} />
                </div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#9333ea', marginBottom: '5px' }}>
                  {service.count}
                </div>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  {service.count} serviços
                </div>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#10b981', marginTop: '5px' }}>
                  R$ {service.revenue.toLocaleString('pt-BR')}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Todos os Clientes */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '20px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
          <h3 style={{ margin: 0, color: '#333' }}>
            Todos os Clientes ({reportData.topClients.length})
          </h3>
          
          {/* Ordenação */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <span style={{ fontSize: '14px', color: '#666' }}>{t('common.sortBy')}</span>
            <select
              value={clientSortBy}
              onChange={(e) => setClientSortBy(e.target.value as 'name' | 'revenue')}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="revenue">💰 Maior Valor</option>
              <option value="name">{t('timeDiscrepancies.alphabetical')}</option>
            </select>
            
            <button
              onClick={() => generateReport('top-clientes')}
              style={{
                backgroundColor: '#9333ea',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Download style={{ fontSize: '16px' }} />
              {t('download')}
            </button>
          </div>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div>{t('common.loadingData')}</div>
          </div>
        ) : sortedClients.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <p>{t('reports.noClients')}</p>
            <p style={{ fontSize: '14px' }}>{t('reports.addClientsHint')}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', maxHeight: '600px', overflowY: 'auto' }}>
            {sortedClients.map((client, index) => (
              <div
                key={client.id}
                style={{
                  border: '1px solid #eee',
                  borderRadius: '8px',
                  padding: '15px',
                  backgroundColor: '#f9f9f9',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: clientSortBy === 'revenue' && index < 3 ? '#9333ea' : '#6b7280',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '15px',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}>
                    {index + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>{client.name}</h4>
                    <div style={{ fontSize: '14px', color: '#666', display: 'flex', gap: '15px' }}>
                      <span>{client.services} {t('services')}</span>
                      {client.phone && <span>📱 <PhoneLink phone={client.phone} showIcon={false} /></span>}
                      {client.email && <span>📧 {client.email}</span>}
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#10b981' }}>
                    R$ {client.revenue.toLocaleString('pt-BR')}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {t('totalBilled')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sugestões de Próximo Serviço */}
      <div style={{ marginBottom: '20px' }}>
        <ServiceSuggestionsPanel suggestions={serviceSuggestions} isLoading={isLoading} />
      </div>

      {/* Relatório de Discrepâncias de Tempo */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, color: '#333' }}>{t('reports.timeDiscrepanciesFull')}</h3>
          <button
            onClick={() => generateReport('discrepancias-tempo')}
            style={{
              backgroundColor: '#9333ea',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Download style={{ fontSize: '16px' }} />
            {t('download')}
          </button>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div>{t('common.loadingData')}</div>
          </div>
        ) : reportData.timeDiscrepancies.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <p>{t('reports.noDiscrepancies')}</p>
            <p style={{ fontSize: '14px' }}>{t('reports.allOnTime')}</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '12px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>{t('timeDiscrepancies.service')}</th>
                  <th style={{ textAlign: 'left', padding: '12px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>{t('common.client')}</th>
                  <th style={{ textAlign: 'center', padding: '12px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Estimado</th>
                  <th style={{ textAlign: 'center', padding: '12px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Real</th>
                  <th style={{ textAlign: 'center', padding: '12px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Diferença</th>
                  <th style={{ textAlign: 'center', padding: '12px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>{t('common.status')}</th>
                </tr>
              </thead>
              <tbody>
                {reportData.timeDiscrepancies.map((item) => (
                    <tr key={item.appointment_id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '12px', fontSize: '14px' }}>
                        {item.service_type.charAt(0).toUpperCase() + item.service_type.slice(1)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px' }}>{item.client_name}</td>
                      <td style={{ padding: '12px', textAlign: 'center', fontSize: '14px' }}>
                        {item.estimated_duration} min
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', fontSize: '14px' }}>
                        {item.actual_duration} min
                      </td>
                      <td style={{ 
                        padding: '12px', 
                        textAlign: 'center', 
                        fontSize: '14px',
                        fontWeight: '600',
                        color: item.difference > 0 ? '#dc2626' : item.difference < 0 ? '#10b981' : '#666'
                      }}>
                        {item.difference > 0 ? '+' : ''}{item.difference} min
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                          backgroundColor: item.status === 'over' ? '#fee2e2' : item.status === 'under' ? '#d1fae5' : '#f3f4f6',
                          color: item.status === 'over' ? '#dc2626' : item.status === 'under' ? '#10b981' : '#666'
                        }}>
                          {item.status === 'over' ? '⚠️ Excedeu' : item.status === 'under' ? '✅ Economizou' : '✅ Exato'}
                        </span>
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Relatorios;

